import React from 'react'
import {
  MdInventory,
  MdShoppingCart,
  MdTrendingUp,
  MdCalendarToday,
} from 'react-icons/md'
import { formatCurrency, formatDate } from '../utils/formatters'
import { filterByDateRange } from '../utils/calculations'
import './Dashboard.scss'

const Dashboard = ({
  products,
  purchases,
  dashboardDateFilter,
  setDashboardDateFilter,
  dashboardStartDate,
  setDashboardStartDate,
  dashboardEndDate,
  setDashboardEndDate,
}) => {
  const getStats = () => {
    let filteredPurchases = purchases

    if (
      dashboardDateFilter === 'custom' &&
      (dashboardStartDate || dashboardEndDate)
    ) {
      filteredPurchases = filterByDateRange(
        purchases,
        dashboardStartDate,
        dashboardEndDate,
      )
    } else if (dashboardDateFilter !== 'custom') {
      const today = new Date()
      let startDate

      switch (dashboardDateFilter) {
        case 'today':
          startDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
          )
          break
        case 'week':
          const dayOfWeek = today.getDay() // 0 = dimanche, 1 = lundi, ...
          const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
          startDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() + diffToMonday,
          )
          startDate.setHours(0, 0, 0, 0)
          break
        case 'month':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1)
          break
        case 'year':
          startDate = new Date(today.getFullYear(), 0, 1)
          break
        default:
          startDate = null
      }

      if (startDate) {
        filteredPurchases = purchases.filter(
          (p) => new Date(p.date) >= startDate,
        )
      }
    }

    const revenue = filteredPurchases.reduce((sum, p) => sum + p.totalAmount, 0)
    const paidAmount = filteredPurchases.reduce(
      (sum, p) => sum + (p.totalPaid || 0),
      0,
    )
    const remainingAmount = filteredPurchases.reduce(
      (sum, p) => sum + (p.remainingAmount || 0),
      0,
    )

    return {
      count: filteredPurchases.length,
      revenue,
      paidAmount,
      remainingAmount,
    }
  }

  const stats = getStats()

  return (
    <div className='content-space'>
      <div className='section-header'>
        <h2 className='section-title'>Dashboard</h2>
      </div>

      <div className='periodCardContent'>
        {/* Sélecteur de période */}
        <div className='card'>
          <h3 className='font-semibold mb-3'>Période d'analyse</h3>
          <div>
            <div className='form-field'>
              <select
                value={dashboardDateFilter}
                onChange={(e) => setDashboardDateFilter(e.target.value)}
              >
                <option value='today'>Aujourd'hui</option>
                <option value='week'>Cette semaine</option>
                <option value='month'>Ce mois</option>
                <option value='year'>Cette année</option>
                <option value='all'>Toutes les données</option>
                <option value='custom'>Période personnalisée</option>
              </select>
              <p>Filtrer les chiffres en fonction d'une période précise.</p>
            </div>
            {dashboardDateFilter === 'custom' && (
              <>
                <div className='form-field'>
                  <label>Date de début</label>
                  <input
                    type='date'
                    value={dashboardStartDate}
                    onChange={(e) => setDashboardStartDate(e.target.value)}
                  />
                </div>
                <div className='form-field'>
                  <label>Date de fin</label>
                  <input
                    type='date'
                    value={dashboardEndDate}
                    onChange={(e) => setDashboardEndDate(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Statistiques principales */}
        <div className='card-grid statContainer cols-4-lg'>
          <div className='firstStat'>
            <div className='card'>
              <div className='stat-card'>
                <div className='stat-icon blue'>
                  <MdInventory className='h-6 w-6' />
                </div>
                <div className='stat-content'>
                  <p className='stat-label'>Produits</p>
                  <p className='stat-value'>{products.length}</p>
                </div>
              </div>
            </div>

            <div className='card'>
              <div className='stat-card'>
                <div className='stat-icon green'>
                  <MdShoppingCart className='h-6 w-6' />
                </div>
                <div className='stat-content'>
                  <p className='stat-label'>Ventes</p>
                  <p className='stat-value'>{stats.count}</p>
                </div>
              </div>
            </div>
          </div>

          <div className='card md-767'>
            <div className='stat-card'>
              <div className='stat-icon blue'>
                <MdInventory className='h-6 w-6' />
              </div>
              <div className='stat-content'>
                <p className='stat-label'>Produits</p>
                <p className='stat-value'>{products.length}</p>
              </div>
            </div>
          </div>

          <div className='card md-767'>
            <div className='stat-card'>
              <div className='stat-icon green'>
                <MdShoppingCart className='h-6 w-6' />
              </div>
              <div className='stat-content'>
                <p className='stat-label'>Ventes</p>
                <p className='stat-value'>{stats.count}</p>
              </div>
            </div>
          </div>

          <div className='card'>
            <div className='stat-card'>
              <div className='stat-icon yellow'>
                <MdTrendingUp className='h-6 w-6' />
              </div>
              <div className='stat-content'>
                <p className='stat-label'>Chiffre d'affaires</p>
                <p className='stat-value lg'>{formatCurrency(stats.revenue)}</p>
              </div>
            </div>
          </div>

          <div className='card'>
            <div className='stat-card'>
              <div className='stat-icon purple'>
                <MdCalendarToday className='h-6 w-6' />
              </div>
              <div className='stat-content'>
                <p className='stat-label'>Reste à percevoir</p>
                <p className='stat-value lg warning'>
                  {formatCurrency(stats.remainingAmount)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques détaillées */}
      <div className='card-grid cols-2-md'>
        <div className='card'>
          <div className='stats-section'>
            <h3 className='stats-header'>Résumé financier</h3>
            <div className='stats-list'>
              <div className='stats-item'>
                <span className='stats-label'>Chiffre d'affaires</span>
                <div className='stats-value'>
                  <div className='value-main'>
                    {formatCurrency(stats.revenue)}
                  </div>
                  <div className='value-sub'>{stats.count} vente(s)</div>
                </div>
              </div>
              <div className='stats-item'>
                <span className='stats-label'>Montant perçu</span>
                <div className='stats-value'>
                  <div className='value-main paidamount'>
                    {formatCurrency(stats.paidAmount)}
                  </div>
                </div>
              </div>
              <div className='stats-item border-top'>
                <span className='stats-label'>Reste à percevoir</span>
                <div className='stats-value'>
                  <div
                    className='value-main'
                    style={{
                      color: stats.remainingAmount > 0 ? '#dc2626' : '#059669',
                    }}
                  >
                    {formatCurrency(stats.remainingAmount)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card'>
          <div className='stats-section'>
            <h3 className='stats-header'>Dernières ventes</h3>
            <div className='recent-sales'>
              {purchases.slice(0, 3).map((purchase) => (
                <div key={purchase.id} className='sale-item'>
                  <div className='sale-info'>
                    <div className='sale-client'>
                      {purchase.clientName.length > 15
                        ? purchase.clientName.substring(0, 15) + '...'
                        : purchase.clientName}
                    </div>
                    <div className='sale-date'>{formatDate(purchase.date)}</div>
                  </div>
                  <div className='sale-amount'>
                    <div className='amount-value'>
                      {formatCurrency(purchase.totalAmount)}
                    </div>
                    <div className='amount-method'>
                      {purchase.paymentMethods
                        ? purchase.paymentMethods
                            .map((pm) => pm.method)
                            .join(', ')
                            .replace(/-/g, ' ')
                        : purchase.paymentMethod?.replace('-', ' ')}
                    </div>
                    {purchase.remainingAmount > 0 && (
                      <div className='remaining-indicator'>
                        Reste: {formatCurrency(purchase.remainingAmount)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {purchases.length === 0 && (
                <p className='text-center pt-4'>Aucune vente enregistrée</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default Dashboard
